/**
 * KIAN-Antworten sind Markdown für den Bildschirm. Vorgelesen klingen Sternchen, Tabellen und
 * Adressen wie Rauschen — hier wird daraus Sprechtext, in Abschnitten, damit der erste Satz
 * klingt, bevor der Rest fertig ist.
 */

/** Längste vorgelesene Antwort — was darüber hinausgeht, steht im Chat. */
export const SPEAK_MAX_CHARS = 1200;

const EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu;

export function toSpeakableText(markdown: string, maxChars = SPEAK_MAX_CHARS): string {
  let t = markdown.replace(/\r\n/g, '\n');
  t = t.replace(/```[\s\S]*?```/g, '\nDer Codeblock steht im Chat.\n');
  t = t.replace(/`([^`]+)`/g, '$1');
  t = t.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  t = t.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  t = t.replace(/https?:\/\/\S+/g, 'ein Link im Chat');
  // Tabellen (Zeilen mit „|") ersetzt ein Hinweis — Zelle für Zelle vorgelesen versteht sie niemand
  t = t.replace(/(?:^[ \t]*\|.*\|[ \t]*(?:\n|$))+/gm, '\nDie Tabelle steht im Chat.\n');
  t = t.replace(/^[ \t]*#{1,6}[ \t]*/gm, '');
  t = t.replace(/^[ \t]*[-*+•][ \t]+/gm, '');
  t = t.replace(/^[ \t]*>[ \t]?/gm, '');
  t = t.replace(/(\*\*|__)(.+?)\1/g, '$2');
  t = t.replace(/\*(\S[^*\n]*?)\*/g, '$1');
  t = t.replace(/(^|\s)_(\S[^_\n]*?)_(?=\s|$|[.,!?])/g, '$1$2');
  t = t.replace(/~~(.+?)~~/g, '$1');
  t = t.replace(EMOJI, '');
  t = t.replace(/\s*→\s*/g, ' nach ').replace(/\s*·\s*/g, ', ');
  // Jede Zeile ein Satz: ohne Satzzeichen am Ende verschmölzen Aufzählungspunkte zu einem Satz
  t = t
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (/[.!?:;,]$/.test(line) ? line : `${line}.`))
    .join(' ');
  t = t.replace(/\s+/g, ' ').replace(/\s+([.,!?;:])/g, '$1').trim();

  if (t.length > maxChars) {
    const cut = t.slice(0, maxChars);
    const lastStop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
    t = `${lastStop > maxChars * 0.5 ? cut.slice(0, lastStop + 1) : `${cut.trim()} …`} Den Rest findest du im Chat.`;
  }
  return t;
}

function splitLong(sentence: string, max: number): string[] {
  const parts: string[] = [];
  let rest = sentence;
  while (rest.length > max) {
    const window = rest.slice(0, max);
    const at = Math.max(window.lastIndexOf(', '), window.lastIndexOf('; '), window.lastIndexOf(' – '));
    const cut = at > max * 0.4 ? at + 1 : Math.max(window.lastIndexOf(' '), 1);
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}

/** Abkürzungen, nach deren Punkt kein Satz endet („z. B.", „ca.", „Nr.") */
const ABBREVIATIONS = new Set([
  'z', 'b', 'u', 'a', 'd', 'h', 'o', 'ä', 's', 'ca', 'nr', 'bzw', 'evtl', 'inkl', 'ggf', 'zzgl', 'bspw', 'vgl', 'usw',
  'etc', 'hr', 'fr', 'dr', 'str', 'tel', 'min', 'max', 'std', 'mio', 'mrd', 'sog', 'abs', 'gem', 'lt', 'ok', 'uvm',
]);

/**
 * In Sätze teilen — ohne an Ordnungszahlen („2. OG", „30. September"), Datumsangaben und
 * Abkürzungen zu brechen: Mitten im Satz abgeschnitten klingt die Stimme wie beim Satzende.
 */
export function splitSentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  const end = /[.!?]+["“”»]?(?=\s|$)/g;
  let m: RegExpExecArray | null;
  while ((m = end.exec(text))) {
    const stop = m.index + m[0].length;
    if (m[0][0] === '.' && m[0].length === 1 && stop < text.length) {
      const lastWord = text.slice(start, m.index).split(/\s+/).pop() ?? '';
      if (/\d$/.test(lastWord)) continue;
      if (ABBREVIATIONS.has(lastWord.toLowerCase().replace(/[^a-zäöüß]/g, ''))) continue;
    }
    out.push(text.slice(start, stop).trim());
    start = stop;
  }
  if (start < text.length) out.push(text.slice(start).trim());
  return out.filter(Boolean);
}

/**
 * In Abschnitte zum Vorlesen teilen. Der erste bleibt kurz (ein Satz), damit sofort etwas zu
 * hören ist; die übrigen fassen Sätze bis `maxChunk` Zeichen zusammen.
 */
export function splitForSpeech(text: string, maxChunk = 240, firstMax = 140): string[] {
  const sentences = splitSentences(text);
  const chunks: string[] = [];
  let current = '';
  for (const sentence of sentences) {
    const limit = chunks.length === 0 ? firstMax : maxChunk;
    if (sentence.length > limit) {
      if (current) {
        chunks.push(current);
        current = '';
      }
      chunks.push(...splitLong(sentence, limit));
      continue;
    }
    if (!current) current = sentence;
    else if (chunks.length > 0 && current.length + 1 + sentence.length <= maxChunk) current = `${current} ${sentence}`;
    else {
      chunks.push(current);
      current = sentence;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}
